import React, { useEffect, useMemo, useState } from 'react';
import { History } from 'history';

import get from 'lodash/get';
import useLazyRest from 'store/api/useLazyRest';
import storage from 'utils/storage';
import {
  cleanupImpersonationSession,
  isImpersonating,
} from 'utils/impersonationSession';

import {
  ACCESS_TOKEN,
  ISB_TOKEN_KEY,
  ISB_USER,
  PERMISSIONS,
  USER,
  VERSIONING,
  setSentryUser,
} from 'store/authentication/session';
import buildClient from 'store/graphql/apolloClient';
import { Permissions, User } from 'components/User/types';
import { hasInternalAppAccess } from 'store/authentication/utils';
import { loadPostHog } from 'components/PostHog/PostHog';
import { Environment } from 'types';
import { PostHog } from 'posthog-js';
import useStorage from './useStorage';
import Context, { Versioning, LoginSession } from './Context';
import useErrorHandlerProvider from './errorHandler/useErrorHandlerProvider';
import { transformUser } from './utils';

import LoggedUserQuery from './LoggedUser';

import jsonVersioning from '../../constants/versioning.json';

type ApplicationProviderProps = WithChildren<{
  history: History;
  environment?: Environment;
}>;

const TIME_LIMIT = 60 * 60 * 1000; // one hour
/* eslint-disable-next-line max-statements, max-lines-per-function */
function ApplicationProvider({
  children,
  history,
  environment,
}: ApplicationProviderProps) {
  const location = history?.location;
  const [token, setToken] = useStorage<string>(ACCESS_TOKEN);
  const [user, setPersistentUser] = useStorage<User>(USER);
  const [isbToken, setIsbToken] = useStorage<string>(ISB_TOKEN_KEY);
  const [isbUser, setIsbUser] = useStorage(ISB_USER);
  const [versioning, setVersioning] = useStorage<Versioning>(VERSIONING);
  const [permissions, setPermissions] = useStorage<Permissions>(PERMISSIONS);
  const errorHandler = useErrorHandlerProvider();
  const [postHog, setPostHog] = useState<PostHog | null>(null);

  const apolloClient = useMemo(() => buildClient(token, permissions), [
    token,
    permissions,
  ]);

  const timeSinceLastUpdate = versioning
    ? Date.now() - versioning.lastUpdate
    : 0;

  useEffect(() => {
    if (!postHog && environment) {
      loadPostHog(environment, setPostHog);
    }
  }, [environment, setPostHog, postHog]);

  const setUser = React.useCallback(
    (u: User | null) => {
      setPersistentUser(transformUser(u));
      setSentryUser(u);
    },
    [setPersistentUser],
  );

  const [loadPermissions, { pending: permissionsIsLoading }] = useLazyRest({
    url: '/users/permissions',
    method: 'get',
    onCompleted: (res) => setPermissions(res),
  });

  const clearLocalStorage = React.useCallback(() => {
    setUser(null);
    setToken(null);
    setPermissions(null);
    storage.remove(USER);
    storage.remove(ACCESS_TOKEN);
    storage.remove(PERMISSIONS);
    storage.remove(VERSIONING);
    // IMPORTANT: Clear impersonation data on logout to prevent confusion
    // This prevents the "impersonating yourself" issue when logging back in
    cleanupImpersonationSession();
  }, [setPermissions, setToken, setUser]);

  const [logout] = useLazyRest({
    url: '/users/logout',
    method: 'post',
    onCompleted: () => {
      clearLocalStorage();
      postHog?.stopSessionRecording();

      if (storage.get('$BEN$--microsoftSSO') === true) {
        window.location.assign(
          `https://login.microsoftonline.com/common/oauth2/v2.0/logout?post_logout_redirect_uri=${window.location.origin}/user/login`,
        );
      }
    },
  });

  const loadUser = React.useCallback(async () => {
    if (!user?.flags?.HasAcceptedWebsiteTerms) {
      return;
    }

    try {
      const res = await apolloClient.query({
        query: LoggedUserQuery,
        variables: {
          userId: user.id,
        },
      });
      if (res.errors) {
        return;
      }
      const loggedinUser: User = res.data.user;

      // Check access using FRESH user data from GraphQL (includes appRouting)
      const hasInternalAccess = hasInternalAppAccess(loggedinUser);

      // Access control applies to ALL users, including impersonated ones
      // If user doesn't have ben-app access, show access denied page
      if (!hasInternalAccess) {
        // User doesn't have ben-app access (showcase-only or no access at all)
        // Redirect to showcase-only access page instead of logging out
        // This allows users to see a friendly message and link to Showcase
        // Don't redirect if we're already on the showcase-only access page to prevent infinite loop
        if (window.location.pathname !== '/user/showcase-only-access') {
          window.location.href = '/user/showcase-only-access';
        }
        return;
      }
      setUser(loggedinUser);

      const additionalData = {
        first_name: loggedinUser.firstName,
        last_name: loggedinUser.lastName,
        email: loggedinUser.email,
        team: loggedinUser.team,
        featureFlags: Object.entries(loggedinUser.featureFlags || {})
          .filter(([, flag]) => flag.status === 'optional')
          .reduce((acc, [name, flag]) => {
            const selection =
              typeof flag.userSelection === 'boolean'
                ? flag.userSelection
                : null;
            acc[name] = selection;
            return acc;
          }, {} as { [key: string]: boolean | null }),
      };

      postHog?.identify(res.data.user.id, additionalData);
      // start the session recording once the user has logged in
      if (environment === 'production') postHog?.startSessionRecording();
    } catch (e) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apolloClient, setUser, user, postHog, environment, logout]);

  function loadUserSession() {
    if (!user?.flags?.HasAcceptedWebsiteTerms) {
      return;
    }
    loadUser();
    loadPermissions();
  }

  const updateVersioning = React.useCallback(() => {
    setVersioning({
      uuid: jsonVersioning.uuid,
      lastUpdate: Date.now(),
    });
  }, [setVersioning]);

  const setSession = React.useCallback(
    (loginSession: LoginSession) => {
      errorHandler.reset();
      setUser(loginSession.user);
      setToken(loginSession.access_token);
      setPermissions(loginSession.permissions);
      updateVersioning();
    },
    [updateVersioning, errorHandler, setUser, setToken, setPermissions],
  );

  const passedLimit = timeSinceLastUpdate > TIME_LIMIT;
  // Consolidated session loading logic to prevent duplicate API calls
  // Previously two separate useEffects were racing on mount, both calling loadUserSession()
  // This caused duplicate calls to /api/users/permissions and all other endpoints
  // See: https://github.com/productplacementtech/pp-monorepo/issues/3446
  useEffect(() => {
    if (!token || location?.pathname === '/isb') return;

    if (passedLimit) {
      loadUserSession();
      updateVersioning();
      return;
    }

    if (versioning?.uuid !== jsonVersioning.uuid) {
      updateVersioning();
      loadUserSession();
      return;
    }

    loadUserSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passedLimit, token, versioning]);

  useEffect(() => {
    if (token && get(errorHandler.errors, '[0].raw.response.status') === 401) {
      clearLocalStorage();
    }
  }, [clearLocalStorage, errorHandler.errors, token]);

  // Clean up impersonation state if there's no valid token
  useEffect(() => {
    if (!token && isImpersonating()) {
      cleanupImpersonationSession();
    }
  }, [token]);

  const value = React.useMemo(
    () => ({
      apolloClient,
      errorHandler,
      isbToken,
      isbUser,
      logout,
      permissions,
      permissionsIsLoading,
      setIsbToken,
      setIsbUser,
      setPermissions,
      setSession,
      setToken: (t: string | null) => {
        setToken(t);
      },
      setUser: (u: User | null) => {
        updateVersioning();
        return setUser(u);
      },
      setVersioning,
      token,
      user,
      versioning,
      history,
      postHog,
      loadUser,
    }),
    [
      apolloClient,
      errorHandler,
      isbToken,
      isbUser,
      logout,
      permissions,
      permissionsIsLoading,
      setIsbToken,
      setIsbUser,
      setPermissions,
      setSession,
      setToken,
      updateVersioning,
      setUser,
      setVersioning,
      token,
      user,
      versioning,
      history,
      postHog,
      loadUser,
    ],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export default ApplicationProvider;
